/**
 * MentorBookingCalendar — member-facing slot picker. Renders bookable
 * slots over the next N days, grouped by day, and lets the member submit
 * a booking request with an optional message.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  useMentorAvailability,
  useBookingRequest,
} from "@/lib/mentors/calendar/useMentorCalendar";
import {
  buildBookableSlots,
  groupSlotsByDay,
} from "@/lib/mentors/calendar/slots";
import type { ProposedSlot } from "@/lib/mentors/calendar/types";

const DAY_HORIZON = 28; // 4 weeks forward

export const MentorBookingCalendar = ({
  mentorUserId,
  app,
}: {
  mentorUserId: string;
  app: string;
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { rules, windows, blackouts, bookedRanges } =
    useMentorAvailability(mentorUserId);
  const request = useBookingRequest();

  const [picked, setPicked] = useState<ProposedSlot | null>(null);
  const [message, setMessage] = useState("");

  const slots = useMemo(() => {
    const start = new Date();
    const end = new Date(Date.now() + DAY_HORIZON * 86_400_000);
    return buildBookableSlots({
      rules: rules.data ?? [],
      windows: windows.data ?? [],
      blackouts: blackouts.data ?? [],
      bookedRanges: bookedRanges.data ?? [],
      rangeStart: start,
      rangeEnd: end,
    });
  }, [rules.data, windows.data, blackouts.data, bookedRanges.data]);

  const grouped = useMemo(() => groupSlotsByDay(slots), [slots]);

  const submit = async () => {
    if (!picked || !user) return;
    try {
      await request.mutateAsync({
        mentorUserId,
        memberId: user.id,
        app,
        proposedStart: picked.start,
        proposedEnd: picked.end,
        message: message.trim() || undefined,
      });
      toast({
        title: "Request sent",
        description: "The mentor will confirm or suggest a different time.",
      });
      setPicked(null);
      setMessage("");
    } catch (e) {
      toast({
        title: "Could not send request",
        description: String(e),
        variant: "destructive",
      });
    }
  };

  if (rules.isLoading || windows.isLoading || blackouts.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading availability…</p>;
  }

  if (!grouped.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          This mentor hasn't published availability yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map(([day, daySlots]) => (
        <Card key={day}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {new Date(day).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {daySlots.map((s) => (
                <Button
                  key={s.start.toISOString()}
                  variant="outline"
                  size="sm"
                  onClick={() => setPicked(s)}
                >
                  {s.start.toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                  {s.label && (
                    <Badge variant="secondary" className="ml-2">
                      {s.label}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request this slot</DialogTitle>
          </DialogHeader>
          {picked && (
            <div className="space-y-3 text-sm">
              <p>
                <strong>{picked.start.toLocaleString()}</strong> →{" "}
                {picked.end.toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
              <Textarea
                placeholder="Tell the mentor what you'd like to talk about (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPicked(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={request.isPending}>
              {request.isPending ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
